import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { tokenManager } from '../../lib/api'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface ComplianceDocument {
  id: number
  name: string
  document_type: string
  template_name: string
  content: string
  created_at: string
}

const ProgramDocumentation: React.FC = () => {
  const { programId } = useParams<{ programId: string }>()
  const navigate = useNavigate()
  const [documents, setDocuments] = useState<ComplianceDocument[]>([])
  const [selectedDocument, setSelectedDocument] = useState<ComplianceDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!programId) return

      try {
        const token = tokenManager.getAccessToken()
        if (!token) {
          navigate('/')
          return
        }

        const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/output-documents/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        })

        if (response.ok) {
          const data = await response.json()
          setDocuments(data || [])
        }
      } catch (error) {
        console.error('Error fetching documents:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDocuments()
  }, [programId, navigate])

  const handleViewDocument = (doc: ComplianceDocument) => {
    setSelectedDocument(doc)
    setEditedContent(doc.content)
    setIsEditing(false)
  }

  const handleSaveDocument = async () => {
    if (!selectedDocument) return
    
    setSaving(true)
    try {
      const token = tokenManager.getAccessToken()
      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/output-documents/${selectedDocument.id}/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: editedContent })
      })
      
      if (response.ok) {
        const updatedDoc = await response.json()
        setDocuments(documents.map(d => d.id === updatedDoc.id ? updatedDoc : d))
        setSelectedDocument(updatedDoc)
        setIsEditing(false)
      } else {
        alert('Failed to save document')
      }
    } catch (error) {
      console.error('Error saving document:', error)
      alert('Error saving document')
    } finally {
      setSaving(false)
    }
  }

  const handleDownloadDocument = async (doc: ComplianceDocument) => {
    try {
      const token = tokenManager.getAccessToken()
      
      // Call backend endpoint to download as DOCX
      const response = await fetch(
        `${API_BASE_URL}/api/programs/${programId}/output-documents/${doc.id}/download/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      )
      
      if (response.ok) {
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${doc.name.replace(/\s+/g, '_')}.docx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } else {
        alert('Failed to download document')
      }
    } catch (error) {
      console.error('Error downloading document:', error)
      alert('Error downloading document')
    }
  }

  const handleDeleteDocument = async (doc: ComplianceDocument) => {
    if (!confirm(`Are you sure you want to delete "${doc.name}"?`)) return
    
    try {
      const token = tokenManager.getAccessToken()
      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/output-documents/${doc.id}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })
      
      if (response.ok) {
        setDocuments(documents.filter(d => d.id !== doc.id))
        if (selectedDocument?.id === doc.id) {
          setSelectedDocument(null)
        }
      } else {
        alert('Failed to delete document')
      }
    } catch (error) {
      console.error('Error deleting document:', error)
      alert('Error deleting document')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading documents...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-6">
          Program Documentation
        </h1>
        
        {documents.length > 0 ? (
          <div className="space-y-4">
            {documents.map(doc => (
              <div key={doc.id} className="bg-card rounded-lg border border-border p-6 hover:border-blue-500 dark:hover:border-blue-500 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {doc.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {doc.template_name || doc.document_type.replace(/_/g, ' ').toUpperCase()}
                    </p>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button 
                      onClick={() => handleViewDocument(doc)}
                      className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" 
                      title="View"
                    >
                      <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleDownloadDocument(doc)}
                      className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" 
                      title="Download"
                    >
                      <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleDeleteDocument(doc)}
                      className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900 transition-colors" 
                      title="Delete"
                    >
                      <svg className="w-5 h-5 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  Created {new Date(doc.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-card rounded-lg border border-border">
            <svg className="w-16 h-16 text-muted-foreground dark:text-slate-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-muted-foreground italic">
              No compliance documents have been generated yet
            </p>
          </div>
        )}
      </div>

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedDocument(null)}
        >
          <div 
            className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  {selectedDocument.name}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedDocument.template_name || selectedDocument.document_type.replace(/_/g, ' ').toUpperCase()}
                </p>
              </div>
              <button
                onClick={() => setSelectedDocument(null)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isEditing ? (
                <div style={{ height: '500px' }}>
                  <ReactQuill
                    theme="snow"
                    value={editedContent}
                    onChange={setEditedContent}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'indent': '-1'}, { 'indent': '+1' }],
                        [{ 'align': [] }],
                        ['link', 'image'],
                        ['clean']
                      ]
                    }}
                    style={{ height: '450px' }}
                  />
                </div>
              ) : (
                <div className="prose dark:prose-invert max-w-none">
                  <div 
                    className="text-sm text-foreground bg-background p-4 rounded-lg"
                    dangerouslySetInnerHTML={{ __html: selectedDocument.content }}
                  />
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between gap-3 p-6 border-t border-border">
              <div className="flex items-center gap-3">
                {isEditing ? (
                  <>
                    <button
                      onClick={handleSaveDocument}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setEditedContent(selectedDocument.content)
                        setIsEditing(false)
                      }}
                      disabled={saving}
                      className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-card-foreground rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleDownloadDocument(selectedDocument)}
                  className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-card-foreground rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                >
                  Download
                </button>
                <button
                  onClick={() => {
                    setSelectedDocument(null)
                    setIsEditing(false)
                  }}
                  className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-card-foreground rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProgramDocumentation

